/**
 * TirvuRede — requisição pela rede de DADOS MÓVEIS, ignorando a rede padrão. Versão web.
 *
 * Mesma API de `modules/tirvu-rede/index.ts` do app nativo (`disponivel`,
 * `redeAtualEhWifi()`, `requisicao()`, tipo `RespostaDadosMoveis`).
 *
 * WEB: o browser não escolhe por qual interface de rede a requisição sai — o desvio pelos
 * dados móveis não existe. É exatamente o caso "sem o nativo" previsto no módulo original
 * (iOS, web, build antigo): `disponivel = false`, `redeAtualEhWifi()` → `false` (o dreno nem
 * tenta o desvio) e `requisicao()` → `{ ok: false, motivo: "indisponivel" }`, nunca lança.
 *
 * Extras web (não existem no nativo): `estadoDaRede()` e `observarRede()` — estado da conexão
 * a partir de `navigator.onLine` e da Network Information API (`navigator.connection`,
 * só Chromium), para diagnóstico/UI.
 */

/** false = sem desvio pelos dados móveis neste aparelho (sempre, no web). */
export const disponivel = false;

/**
 * A rede padrão do app é Wi-Fi? `false` também quando não dá para saber.
 * WEB: sempre `false` — mesmo sabendo que é Wi-Fi (Chromium), não há como desviar a batida
 * pelos dados móveis, e o dreno só usa essa resposta para decidir esse desvio.
 */
export async function redeAtualEhWifi(): Promise<boolean> {
  return false;
}

export type RespostaDadosMoveis =
  | { ok: true; status: number; corpo: string; tipoConteudo: string }
  | {
      ok: false;
      motivo: "indisponivel" | "sem_rede" | "erro";
      mensagem: string;
    };

/**
 * Uma requisição pela rede celular. No web: sempre `ok: false, motivo: "indisponivel"`
 * (texto idêntico ao do módulo nativo sem o nativo). Nunca lança.
 */
export async function requisicao(_opcoes: {
  url: string;
  metodo: "GET" | "POST";
  cabecalhos: Record<string, string>;
  corpo: string | null;
  timeoutMs: number;
}): Promise<RespostaDadosMoveis> {
  return {
    ok: false,
    motivo: "indisponivel",
    mensagem: "Envio pelos dados móveis indisponível neste aparelho",
  };
}

// ── Extras web ──────────────────────────────────────────────────────────────

export type TipoConexaoWeb = "wifi" | "cellular" | "ethernet" | "bluetooth" | "wimax" | "none" | "other" | "unknown";

export type EstadoRedeWeb = {
  /** `navigator.onLine` — `false` é confiável (sem rede); `true` só diz que há ALGUMA rede. */
  online: boolean;
  /** Tipo físico (Chromium Android); `"unknown"` quando o browser não informa. */
  tipo: TipoConexaoWeb;
  /** Qualidade estimada ("slow-2g" | "2g" | "3g" | "4g"), ou null. */
  tipoEfetivo: string | null;
  /** Banda estimada (Mbps) e RTT (ms), ou null. */
  downlinkMbps: number | null;
  rttMs: number | null;
  /** Modo "economia de dados" ligado. */
  economiaDeDados: boolean;
};

type NetworkInformationWeb = EventTarget & {
  type?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
};

function conexao(): NetworkInformationWeb | undefined {
  if (typeof navigator === "undefined") return undefined;
  return (navigator as any).connection as NetworkInformationWeb | undefined;
}

export function estadoDaRede(): EstadoRedeWeb {
  const c = conexao();
  const online = typeof navigator === "undefined" ? true : navigator.onLine !== false;
  const tiposValidos: TipoConexaoWeb[] = ["wifi", "cellular", "ethernet", "bluetooth", "wimax", "none", "other"];
  const tipoBruto = (c?.type ?? "unknown") as TipoConexaoWeb;
  return {
    online,
    tipo: online ? (tiposValidos.includes(tipoBruto) ? tipoBruto : "unknown") : "none",
    tipoEfetivo: c?.effectiveType ?? null,
    downlinkMbps: typeof c?.downlink === "number" ? c.downlink : null,
    rttMs: typeof c?.rtt === "number" ? c.rtt : null,
    economiaDeDados: Boolean(c?.saveData),
  };
}

/** Avisa mudanças de rede (online/offline e troca de conexão). Devolve a função de cancelar. */
export function observarRede(cb: (estado: EstadoRedeWeb) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const avisar = () => {
    try {
      cb(estadoDaRede());
    } catch (e) {
      console.error("[TirvuRede] ouvinte de rede falhou", e);
    }
  };
  window.addEventListener("online", avisar);
  window.addEventListener("offline", avisar);
  const c = conexao();
  c?.addEventListener?.("change", avisar);
  return () => {
    window.removeEventListener("online", avisar);
    window.removeEventListener("offline", avisar);
    c?.removeEventListener?.("change", avisar);
  };
}

const TirvuRede = { disponivel, redeAtualEhWifi, requisicao, estadoDaRede, observarRede };

export default TirvuRede;
